import React from "react";
import { Box, Paper, Typography } from "@mui/material";

export type TableHeaderConfig = {
  key: string;
  label: string;
  align?: "left" | "center" | "right";
  width?: string | number;
  sortable?: boolean;
  sortKey?: string;
  render?: (row: any) => React.ReactNode;
};

export type TableRow = Record<string, React.ReactNode>;

export type SortDirection = "asc" | "desc" | null;

export interface TableSortState {
  key: string | null;
  direction: SortDirection;
}

export interface TableProps<T extends TableRow = TableRow> {
  headers: TableHeaderConfig[];
  data: T[];
  getRowKey?: (row: T, index: number) => React.Key;
  emptyState?: React.ReactNode;
  title?: string;
  sortState?: TableSortState;
  onSortChange?: (sortKey: string, direction: SortDirection) => void;
  gridTemplateColumns?: string;
  variant?: "default" | "cards";
}

/**
 * StatusDot helper component for consistent Status chips with dots
 */
export const StatusDot: React.FC<{ color: string; label: string }> = ({
  color,
  label,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
    <Box
      sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color }}
    />
    <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
      {label}
    </Typography>
  </Box>
);

const Table = <T extends TableRow>({
  headers,
  data,
  getRowKey,
  emptyState,
  title,
  sortState,
  onSortChange,
  gridTemplateColumns: customGrid,
  variant = "default",
}: TableProps<T>) => {
  const gridTemplate = React.useMemo(
    () =>
      customGrid ??
      headers.map((h) => h.width ?? "minmax(120px, 1fr)").join(" "),
    [headers, customGrid],
  );

  if (!headers.length) return null;

  const resolveRowKey = (row: T, index: number) =>
    getRowKey ? getRowKey(row, index) : index;

  const handleHeaderClick = (header: TableHeaderConfig) => {
    if (!header.sortable || !onSortChange) {
      return;
    }
    const sortKey = header.sortKey ?? header.key;
    let nextDirection: SortDirection = "asc";
    if (sortState?.key === sortKey) {
      if (sortState.direction === "asc") {
        nextDirection = "desc";
      } else if (sortState.direction === "desc") {
        nextDirection = null;
      }
    }
    onSortChange(sortKey, nextDirection);
  };

  const isCards = variant === "cards";

  return (
    <Box width="100%" display="flex" flexDirection="column" gap={1.5}>
      {/* Title */}
      {title && (
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      )}

      {/* X Scroll Container */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "hidden",
        }}
      >
        <Box sx={{ display: "inline-block", minWidth: "100%" }}>
          <Box
            sx={{
              display: "inline-block",
              minWidth: "100%",
              width: "100%",
            }}
          >
            {/* Header - Transparent in card mode, shaded in default */}
            <Box
              display="grid"
              gridTemplateColumns={gridTemplate}
              columnGap={2}
              alignItems="center"
              px={2}
              py={1.5}
              sx={{
                backgroundColor: "#f8fcff",
                borderTopLeftRadius: isCards ? 0 : 4,
                borderTopRightRadius: isCards ? 0 : 4,
                border: !isCards ? "1px solid" : "none",
                borderColor: !isCards ? "divider" : "none",
              }}
            >
              {headers.map((header) => {
                const sortKey = header.sortKey ?? header.key;
                return (
                  <Box
                    key={header.key}
                    display="flex"
                    alignItems="center"
                    justifyContent={
                      header.align === "right"
                        ? "flex-end"
                        : header.align === "center"
                          ? "center"
                          : "flex-start"
                    }
                    sx={{
                      cursor: header.sortable ? "pointer" : "default",
                      userSelect: header.sortable ? "none" : "text",
                      color:
                        header.sortable &&
                        sortState?.key === sortKey &&
                        sortState.direction
                          ? "primary.main"
                          : "inherit",
                    }}
                    onClick={() => handleHeaderClick(header)}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      whiteSpace="nowrap"
                      sx={{
                        textTransform: "uppercase",
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {header.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Content Container */}
            <Box
              sx={
                isCards
                  ? { pt: 1 }
                  : {
                      border: "1px solid",
                      borderColor: "divider",
                      borderTop: "none",
                      borderBottomLeftRadius: 4,
                      borderBottomRightRadius: 4,
                      backgroundColor: "#fff",
                    }
              }
            >
              {/* Empty State */}
              {data.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    px: 3,
                    py: 4,
                    borderRadius: 1,
                    border: isCards ? "1px solid" : "none",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {emptyState ?? "No data available yet."}
                  </Typography>
                </Paper>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: isCards ? 1 : 0,
                  }}
                >
                  {data.map((row, index) => {
                    const rowContent = (
                      <Box
                        key={resolveRowKey(row, index)}
                        display="grid"
                        gridTemplateColumns={gridTemplate}
                        columnGap={2}
                        alignItems="center"
                        px={2}
                        py={1}
                        sx={{
                          transition: "background-color 0.2s",
                          boxShadow: "rgba(17, 17, 26, 0.1) 0px 0px 16px",
                          "&:hover": {
                            backgroundColor: "rgba(0,128,240,0.01)",
                          },
                          borderBottom:
                            !isCards && index === data.length - 1
                              ? "none"
                              : "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        {headers.map((header) => (
                          <Box
                            key={header.key}
                            component="div"
                            textAlign={header.align ?? "left"}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "normal",
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "text.primary",
                            }}
                          >
                            {header.render
                              ? header.render(row)
                              : (row[header.key] ?? "—")}
                          </Box>
                        ))}
                      </Box>
                    );

                    return isCards ? (
                      <Paper
                        key={resolveRowKey(row, index)}
                        elevation={0}
                        sx={{
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          overflow: "hidden",
                          transition: "box-shadow 0.2s",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                          },
                        }}
                      >
                        {rowContent}
                      </Paper>
                    ) : (
                      rowContent
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Table;
